import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faEye,
  faEyeSlash,
  faQuestionCircle,
  faSearch,
  faArrowUp,
  faArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import { supabase } from "../../lib/supabase";

const FAQManager = () => {
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFaq, setEditingFaq] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const [formData, setFormData] = useState({
    question: "",
    answer: "",
    category: "",
    is_active: true,
  });

  const categories = [
    "Programări",
    "Servicii",
    "Plăți",
    "Rezultate",
    "Generală",
    "COVID-19",
    "Investigații",
  ];

  useEffect(() => {
    loadFaqItems();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFaqItems = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("faq_items")
        .select("*")
        .order("order_index", { ascending: true });

      if (error) throw error;
      setFaqItems(data || []);
    } catch (error) {
      console.error("Error loading FAQ items:", error);
      showMessage("Eroare la încărcarea întrebărilor", "error");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = "success") => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
      setMessageType("");
    }, 3000);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const resetForm = () => {
    setFormData({
      question: "",
      answer: "",
      category: "",
      is_active: true,
    });
    setEditingFaq(null);
    setShowAddForm(false);
  };

  const handleAddFaq = async (e) => {
    e.preventDefault();
    try {
      const newOrderIndex =
        Math.max(...faqItems.map((f) => f.order_index || 0), 0) + 1;

      const { data, error } = await supabase
        .from("faq_items")
        .insert([
          {
            ...formData,
            order_index: newOrderIndex,
          },
        ])
        .select();

      if (error) throw error;

      setFaqItems([...faqItems, ...data]);
      resetForm();
      showMessage("Întrebare adăugată cu succes!");
    } catch (error) {
      console.error("Error adding FAQ item:", error);
      showMessage("Eroare la adăugarea întrebării", "error");
    }
  };

  const handleEditFaq = (faq) => {
    setFormData({
      question: faq.question || "",
      answer: faq.answer || "",
      category: faq.category || "",
      is_active: faq.is_active !== false,
    });
    setEditingFaq(faq.id);
    setShowAddForm(false);
  };

  const handleUpdateFaq = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from("faq_items")
        .update(formData)
        .eq("id", editingFaq);

      if (error) throw error;

      setFaqItems(
        faqItems.map((f) =>
          f.id === editingFaq ? { ...f, ...formData } : f
        )
      );
      resetForm();
      showMessage("Întrebare actualizată cu succes!");
    } catch (error) {
      console.error("Error updating FAQ item:", error);
      showMessage("Eroare la actualizarea întrebării", "error");
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm("Sigur doriți să ștergeți această întrebare?")) return;

    try {
      const { error } = await supabase
        .from("faq_items")
        .delete()
        .eq("id", faqId);

      if (error) throw error;

      setFaqItems(faqItems.filter((f) => f.id !== faqId));
      showMessage("Întrebare ștearsă cu succes!");
    } catch (error) {
      console.error("Error deleting FAQ item:", error);
      showMessage("Eroare la ștergerea întrebării", "error");
    }
  };

  const handleToggleActive = async (faqId, currentStatus) => {
    try {
      const { error } = await supabase
        .from("faq_items")
        .update({ is_active: !currentStatus })
        .eq("id", faqId);

      if (error) throw error;

      setFaqItems(
        faqItems.map((f) =>
          f.id === faqId ? { ...f, is_active: !currentStatus } : f
        )
      );
      showMessage(`Întrebare ${!currentStatus ? "activată" : "dezactivată"}!`);
    } catch (error) {
      console.error("Error toggling FAQ status:", error);
      showMessage("Eroare la schimbarea statusului", "error");
    }
  };

  const handleReorderFaq = async (faqId, direction) => {
    const faqIndex = faqItems.findIndex((f) => f.id === faqId);
    const newIndex = direction === "up" ? faqIndex - 1 : faqIndex + 1;
    
    if (newIndex < 0 || newIndex >= faqItems.length) return;

    try {
      const currentFaq = faqItems[faqIndex];
      const targetFaq = faqItems[newIndex];

      await supabase
        .from("faq_items")
        .update({ order_index: targetFaq.order_index })
        .eq("id", currentFaq.id);

      await supabase
        .from("faq_items")
        .update({ order_index: currentFaq.order_index })
        .eq("id", targetFaq.id);

      loadFaqItems();
      showMessage("Ordinea întrebărilor a fost actualizată!");
    } catch (error) {
      console.error("Error reordering FAQ:", error);
      showMessage("Eroare la schimbarea ordinii", "error");
    }
  };

  // Filter FAQ items
  const filteredFaqs = faqItems.filter((faq) => {
    const matchesSearch = faq.question
      .toLowerCase()
      .includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "" || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Group by category
  const groupedFaqs = filteredFaqs.reduce((acc, faq) => {
    const category = faq.category || "Fără categorie";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(faq);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Se încarcă...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="header-content">
          <FontAwesomeIcon icon={faQuestionCircle} className="header-icon" />
          <div>
            <h1>Manager FAQ</h1>
            <p>Gestionează întrebările frecvente</p>
          </div>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowAddForm(true)}
        >
          <FontAwesomeIcon icon={faPlus} /> Adaugă Întrebare
        </button>
      </div>

      {message && (
        <div className={`alert alert-${messageType === "error" ? "danger" : "success"}`}>
          {message}
        </div>
      )}

      {/* Search and Filter */}
      <div className="admin-filters">
        <div className="row">
          <div className="col-md-6">
            <div className="search-box">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                className="form-control"
                placeholder="Caută în întrebări și răspunsuri..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">Toate categoriile</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <div className="results-count">
              {filteredFaqs.length} rezultate
            </div>
          </div>
        </div>
      </div>

      {(showAddForm || editingFaq) && (
        <div className="admin-form-container">
          <form onSubmit={editingFaq ? handleUpdateFaq : handleAddFaq}>
            <div className="form-header">
              <h3>
                {editingFaq ? "Editează Întrebare" : "Adaugă Întrebare Nouă"}
              </h3>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={resetForm}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="mb-3">
              <label className="form-label">Întrebare</label>
              <input
                type="text"
                className="form-control"
                name="question"
                value={formData.question}
                onChange={handleInputChange}
                placeholder="ex: Cum pot face o programare?"
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Răspuns</label>
              <textarea
                className="form-control"
                name="answer"
                value={formData.answer}
                onChange={handleInputChange}
                rows="4"
                placeholder="Răspunsul complet la întrebare..."
                required
              />
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Categorie</label>
                  <select
                    className="form-select"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Selectează categoria</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <div className="form-check" style={{ marginTop: "2rem" }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label">
                      Întrebare activă (vizibilă pe site)
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                <FontAwesomeIcon icon={faSave} />{" "}
                {editingFaq ? "Actualizează" : "Adaugă"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={resetForm}
              >
                Anulează
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-content">
        {Object.keys(groupedFaqs).length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faQuestionCircle} size="3x" className="text-muted mb-3" />
            <h3>Nicio întrebare găsită</h3>
            <p>Adaugă prima întrebare în FAQ.</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowAddForm(true)}
            >
              <FontAwesomeIcon icon={faPlus} /> Adaugă Prima Întrebare
            </button>
          </div>
        ) : (
          Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
            <div key={category} className="category-section">
              <h3 className="category-title">{category}</h3>
              <div className="faq-list">
                {categoryFaqs.map((faq, index) => (
                  <div key={faq.id} className={`faq-item ${!faq.is_active ? 'inactive' : ''}`}>
                    <div className="faq-header">
                      <div className="faq-question">
                        <FontAwesomeIcon icon={faQuestionCircle} className="text-primary me-2" />
                        <strong>{faq.question}</strong>
                      </div>
                      <div className="faq-actions">
                        <div className="order-buttons">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleReorderFaq(faq.id, "up")}
                            disabled={index === 0}
                            title="Mută în sus"
                          >
                            <FontAwesomeIcon icon={faArrowUp} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleReorderFaq(faq.id, "down")}
                            disabled={index === categoryFaqs.length - 1}
                            title="Mută în jos"
                          >
                            <FontAwesomeIcon icon={faArrowDown} />
                          </button>
                        </div>
                        <span className={`status-badge ${faq.is_active ? 'active' : 'inactive'}`}>
                          {faq.is_active ? 'ACTIV' : 'INACTIV'}
                        </span>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleToggleActive(faq.id, faq.is_active)}
                            title={faq.is_active ? "Dezactivează" : "Activează"}
                          >
                            <FontAwesomeIcon icon={faq.is_active ? faEyeSlash : faEye} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleEditFaq(faq)}
                            title="Editează"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteFaq(faq.id)}
                            title="Șterge"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FAQManager; 